import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TabCard } from '@/components/layout/tab-card'
import { StatusTabs } from '@/components/ui/status-tabs'
import { Typography } from '@/components/ui/typography'
import { DirtyFieldChange, useDirtyFormGuard } from '@/hooks'

import { CredentialsProvider, PROVIDER_CARDS } from './credentials.constants'

interface CredentialsTabProps {
  buildingId: number
  isAdmin: boolean
  onDirtyChange: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
}

export const CredentialsTab = ({ buildingId, isAdmin, onDirtyChange }: CredentialsTabProps) => {
  const { t } = useTranslation(['organisation'])
  const [provider, setProvider] = useState<CredentialsProvider>('brivo')
  const [isDirty, setIsDirty] = useState(false)
  const [getChanges, setGetChanges] = useState<(() => DirtyFieldChange[]) | undefined>()
  const guard = useDirtyFormGuard(isDirty, getChanges)

  const handleDirtyChange = useCallback(
    (dirty: boolean, getter?: () => DirtyFieldChange[]) => {
      setIsDirty(dirty)
      setGetChanges(dirty && getter ? () => getter : undefined)
      onDirtyChange(dirty, getter)
    },
    [onDirtyChange],
  )

  const handleProviderChange = (next: CredentialsProvider) => {
    if (next === provider) return
    guard(() => {
      handleDirtyChange(false)
      setProvider(next)
    })
  }

  const options: { label: string; value: CredentialsProvider }[] = [
    { label: t('organisation:places.integrations.keyless.types.brivo'), value: 'brivo' },
    { label: t('organisation:places.integrations.keyless.types.saltoSpace'), value: 'saltoSpace' },
    { label: t('organisation:places.integrations.keyless.types.swiftlane'), value: 'swiftlane' },
    {
      label: t('organisation:places.integrations.keyless.types.pointCentral'),
      value: 'pointCentral',
    },
    { label: t('organisation:places.integrations.keyless.types.dormakaba'), value: 'dormakaba' },
    { label: t('organisation:places.integrations.keyless.types.accentra'), value: 'accentra' },
    { label: t('organisation:places.integrations.keyless.types.iglooWork'), value: 'iglooWork' },
  ]

  if (!isAdmin) {
    return (
      <TabCard title={t('organisation:places.accessControl.credentials.title')}>
        <Typography size="text-small" color="black500">
          {t('organisation:places.accessControl.credentials.adminOnly')}
        </Typography>
      </TabCard>
    )
  }

  const tabs = <StatusTabs options={options} value={provider} onChange={handleProviderChange} />
  const Card = PROVIDER_CARDS[provider]

  return <Card buildingId={buildingId} tabs={tabs} onDirtyChange={handleDirtyChange} />
}
